"use client";

import { useState } from "react";
import { useTheme } from "next-themes";
import { useTranslations, useLocale } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { useSession } from "@/hooks/use-session";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { useSiteFlavor } from "@/components/layout/site-flavor-provider";
import { externalLinkProps, useSupportLink } from "@/hooks/use-support-link";
import { wordmarkFor } from "@/lib/brand";
import posthog from "posthog-js";
import { SignupCta, useSignupFlow } from "@/components/landing/signup-flow";
import { MockupPicture } from "@/components/landing/mockup-picture";

/** Support entry point: Telegram on mint24, email on the US surface. */
function SupportLink({ className }: { className?: string }) {
  const t = useTranslations("landing");
  const link = useSupportLink();
  if (!link) return null;

  return (
    <a
      href={link.href}
      {...externalLinkProps(link.isExternal)}
      onClick={() => posthog.capture("landing_click", { element: "support_nav" })}
      className={className}
    >
      {t("nav.support")}
    </a>
  );
}

export function LandingNav() {
  const t = useTranslations("landing");
  const locale = useLocale();
  const { isAuthenticated } = useSession();
  const { brand, isGetmint } = useSiteFlavor();
  const { resolvedTheme } = useTheme();
  const navigates = useSignupFlow().href !== null;
  const [loading, setLoading] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-landing-border/50 bg-landing-bg/80 backdrop-blur-xl">
      <div className="mx-auto flex h-auto min-h-16 max-w-7xl items-center justify-between px-6 py-2">
        <Link href="/" className="flex items-center transition-opacity hover:opacity-80">
          <MockupPicture
            src={wordmarkFor(brand, locale)}
            alt={brand.name}
            // Painted at h-7 (28px) — the widest wordmark is ~3:1, so ~85px.
            sizes="85px"
            priority
            className={cn("h-7 w-auto", resolvedTheme === "dark" && "invert")}
          />
        </Link>

        {/* Desktop: single row */}
        <div className="hidden sm:flex items-center gap-3">
          {isGetmint && (
            <Link
              href="/pricing"
              onClick={() => posthog.capture("landing_click", { element: "pricing_nav" })}
              className="rounded-sm text-sm font-medium text-landing-muted transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-mint"
            >
              {t("nav.pricing")}
            </Link>
          )}
          <LangSwitcher />
          <SupportLink className="text-sm font-medium text-landing-muted transition-colors hover:text-ink" />
          <ThemeToggle />
          <SignupCta
            onPress={() => {
              posthog.capture("landing_click", { element: "cta_nav" });
              if (!isAuthenticated) posthog.capture("sign_in_clicked", { source: "nav" });
              // The spinner means "we are navigating"; opening the tour is
              // instant, so it would just flash.
              if (navigates) setLoading(true);
            }}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-mint px-5 py-2 text-sm font-semibold text-[#052e25] transition-all hover:bg-mint-dark active:scale-95 active:opacity-80 touch-manipulation"
            style={{ minWidth: "120px" }}
          >
            {loading ? (
              <>
                <span className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border-2 border-[#052e25]/30 border-t-[#052e25] animate-spin" />
                <span>{t("nav.ctaLoading")}</span>
              </>
            ) : (
              t("nav.tryFree")
            )}
          </SignupCta>
        </div>

        {/* Mobile: support + CTA only */}
        <div className="flex sm:hidden items-center gap-2">
          <SupportLink className="text-sm font-medium text-landing-muted transition-colors hover:text-ink" />
          <SignupCta
            onPress={() => {
              posthog.capture("landing_click", { element: "cta_nav" });
              if (!isAuthenticated) posthog.capture("sign_in_clicked", { source: "nav" });
              // The spinner means "we are navigating"; opening the tour is
              // instant, so it would just flash.
              if (navigates) setLoading(true);
            }}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-mint px-5 py-2 text-sm font-semibold text-[#052e25] transition-all hover:bg-mint-dark active:scale-95 active:opacity-80 touch-manipulation"
            style={{ minWidth: "120px" }}
          >
            {loading ? (
              <>
                <span className="inline-block h-3.5 w-3.5 shrink-0 rounded-full border-2 border-[#052e25]/30 border-t-[#052e25] animate-spin" />
                <span>{t("nav.ctaLoading")}</span>
              </>
            ) : (
              t("nav.tryFree")
            )}
          </SignupCta>
        </div>
      </div>
    </nav>
  );
}

export function LangSwitcher() {
  const locale = useLocale();
  const pathname = usePathname();
  const { isGetmint } = useSiteFlavor();
  if (isGetmint) return null;

  return (
    <div className="flex rounded-full border border-landing-border bg-landing-card p-0.5">
      <Link
        href={pathname}
        locale="ru"
        onClick={() => posthog.capture("landing_click", { element: "lang_ru" })}
        className={cn(
          "rounded-full px-3 py-1 text-xs font-medium transition-colors",
          locale === "ru"
            ? "bg-ink/10 text-ink"
            : "text-landing-muted hover:text-ink"
        )}
      >
        RU
      </Link>
      <Link
        href={pathname}
        locale="en"
        onClick={() => posthog.capture("landing_click", { element: "lang_en" })}
        className={cn(
          "rounded-full px-3 py-1 text-xs font-medium transition-colors",
          locale === "en"
            ? "bg-ink/10 text-ink"
            : "text-landing-muted hover:text-ink"
        )}
      >
        EN
      </Link>
    </div>
  );
}
